import React, { useMemo } from 'react';
import styled from 'styled-components';
import Box from '@clasohlson/co-ui-components/Box';
import Typography from '@clasohlson/co-ui-components/Typography';
// import TextField from '@clasohlson/co-ui-components/TextField';
import InlineStack from '@clasohlson/co-ui-components/InlineStack';
// import { useTranslation } from 'react-i18next';
import { LineItem } from '../../orderModel';
import { formatSkuNumber, formatPrice, getCountryCodeFromUrl, useTranslation } from '../../utils';
import { CustomRow } from '../layout/CustomRow';
import { CustomColumn } from '../layout/CustomColumn';
import { CustomCheckbox } from '../layout/CustomCheckbox';
import { AlertCircle } from '../icons/AlertCircle';
import RetTypography from '../layout/RetTypography';
// import NumberInput from '@clasohlson/co-ui-components/NumberInput';
import LazyImage from '@clasohlson/co-ui-components/LazyImage';
import NumberOfInput from '../layout/NumberOfInput';
import { useGlobalStateValue } from '../../store/globalState';

interface StyledSelectableBoxProps {
  isValid: boolean;
  isSelected: boolean;
  disabled: boolean;
}

const getBackgroundColor = (isSelected: boolean, isValid: boolean) => {
  if (isSelected && isValid) {
    return '#E5F9ED';
  }

  if (isSelected && !isValid) {
    return '#FF00000D';
  }

  return '#FFF';
};

const SelectableBox = styled(Box)<StyledSelectableBoxProps>`
  background: ${({ isSelected, isValid }) => getBackgroundColor(isSelected, isValid)};
  border-width: 0 0 1px 0;
  border-style: solid;
  border-color: ${({ theme }) => theme.colors.mediumGray};
  // opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  /* border-color: ${({ isSelected, theme }) =>
    isSelected ? theme.colors.primary : 'transparent'}; */
  min-height: 100px;
  img {
    height: auto;
    max-width: 90px;
    width: 100%;
  }
  a {
    text-decoration: none;
  }
  .disabled-text {
    margin-top: 7px;
  }
  ${({ disabled }) =>
    disabled &&
    `
    .fade-item {
      opacity: 0.48;
    }
  `}
`;

// const CustomNumberInput = styled.div`
//   div {
//     height: 2rem;
//     min-width: 5rem;
//     background: #f7f7f7;
//   }
//   input {
//     font-size: 14px;
//   }
//   button {
//     background: #f7f7f7;
//     color: #000;
//     line-height: 100%;
//     height: auto;
//     &:first-of-type,
//     &:last-of-type {
//       border: 0;
//     }
//   }
//   @media screen and (max-width: 600px) {
//     div {
//       height: 1.75rem;
//     }
//   }
// `;

const StyledColumn = styled(CustomColumn)`
  &.price-col {
    /* input {
      text-align: center;
      height: 1.5rem;
      line-height: 1.5rem;
      width: 1.5rem;
      padding: 0;
      justify-self: flex-end;
      margin-left: auto;
      display: flex;
      margin-bottom: 0.25rem;
      font-size: 0.75rem;
    } */
    .price {
      display: flex;
      align-self: flex-end;
      justify-self: flex-end;
      margin-top: auto;
      margin-left: auto;
    }
  }
  @media screen and (max-width: 600px) {
    padding: 1rem 0.25rem;
    &.image-col {
      width: 22%;
    }
    &.content-col {
      width: 50%;
    }
    &.price-col {
      width: 28%;
      .h4 {
        font-size: 1rem;
      }
    }
  }
`;

const ImageCheck = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  position: relative;
  flex-grow: 1;
  img {
    max-width: 80%;
    &.has-error {
      opacity: 0.1;
    }
  }
  @media screen and (max-width: 600px) {
    img {
      max-width: 100%;
    }
    > div {
      display: flex;
      position: absolute;
      width: 100%;
      height: 100%;
      left: 0;
      top: 0;
      background: rgba(0, 0, 0, 0.35);
      align-items: center;
      justify-content: center;

      label:before {
        margin-right: 0;
        background: #fff;
      }
    }
  }
`;

const ProductTitle = styled.span`
  font-size: 13px;
  font-weight: bold;
  line-height: 1.23;
  a {
    text-decoration: none !important;
  }
`;

const Price = styled.span`
  font-size: 18px;
  font-weight: bold;
  line-height: 1.28;
`;

const RowWrapper = styled.div`
  position: relative;
`;

const QtyWrapper = styled.div`
  position: absolute;
  right: 0.75rem;
  top: 0.75rem;
  z-index: 5;
`;
interface Props {
  product: LineItem;
  selected?: boolean;
  disabled?: boolean;
  handleQtyChange: (prod: LineItem, num: number) => void;
  handleClick?: (prod: LineItem) => void;
}

export const ProductItem: React.FC<Props> = ({
  product,
  selected,
  disabled = false,
  handleQtyChange,
  handleClick = () => null,
}) => {
  const {
    state: { returns },
  } = useGlobalStateValue();
  // const [returnQty, setReturnQty] = useState<number>(product.deliveredQuantity === 1 ? 1 : 0);
  const { t } = useTranslation();

  const returnQty = returns.find((p) => p.rowNumber === product.rowNumber)?.qtyToReturn || 0;

  const isValid = useMemo(() => {
    return selected && returnQty > 0;
  }, [selected, returnQty]);

  // const toggleArt = (prod: LineItem) => {
  //   if (rowIsSelected(prod.rowNumber)) {
  //     dispatch({ type: REMOVE_RETURN, payload: prod.rowNumber });
  //   } else {
  //     const ret: IReturn = { rowNumber: prod.rowNumber, qtyToReturn: prod.quantity };
  //     dispatch({ type: ADD_OR_UPDATE_RETURN, payload: ret });
  //   }
  // };

  const qtyChange = (num: number) => {
    // setReturnQty(num);
    handleQtyChange(product, num);
  };

  const selectProduct = (e: any, prod: LineItem) => {
    e.preventDefault();
    e.stopPropagation();
    handleClick(prod);
  };
  const image = product?.mainImage || `${formatSkuNumber(product.sku)}.jpg`;
  return (
    <RowWrapper>
      <QtyWrapper>
        {/* <CustomNumberInput> */}
        <NumberOfInput
          isValid={!!isValid}
          disabled={!selected}
          min={product.deliveredQuantity === 1 ? 1 : 0}
          max={product.deliveredQuantity}
          value={returnQty}
          handleChange={(num) => qtyChange(num)}
          data-testid={`${product.sku}-returnQty`}
          increaseButtonTestId={`${product.sku}-returnQty-increase`}
          decreaseButtonTestId={`${product.sku}-returnQty-decrease`}
        />
        {/* <NumberInput
            disabled={!selected}
            min={1}
            max={product.deliveredQuantity}
            value={returnQty}
            label={t('numberOfItems')}
            handleChange={(num) => qtyChange(num)}
            hideLabel
            data-testid={`${product.sku}-returnQty`}
          /> */}
        {/* </CustomNumberInput> */}
      </QtyWrapper>
      <SelectableBox
        isSelected={!!selected}
        isValid={!!isValid}
        topSpacing="medium"
        leftSpacing="medium"
        rightSpacing="medium"
        bottomSpacing="none"
        disabled={disabled}
        onClick={(e) => !disabled && selectProduct(e, product)}
        data-testid={`prod-${product.sku}`}
      >
        <CustomRow>
          <StyledColumn width={15} className="image-col">
            <ImageCheck className="fade-item">
              <div>
                <CustomCheckbox
                  id={`prod-${product.sku}`}
                  checked={selected}
                  onChange={(e) => !disabled && selectProduct(e, product)}
                  data-testid={`check-${product.sku}`}
                />
                {/* <Checkbox
                id={`prod-${product.sku}`}
                checked={selected}
                label="Vald"
                hideLabel
                onChange={(e) => !disabled && selectProduct(e, product)}
              /> */}
              </div>
              <LazyImage
                src={`http://cdn.clasohlson.com/productimages/303x303/${image}`}
                alt={product.sku}
              />
              {/* <img src={`http://cdn.clasohlson.com/productimages/600x600/${image}`} /> */}
            </ImageCheck>
          </StyledColumn>
          <StyledColumn width={65} className="content-col">
            <ProductTitle className="fade-item">
              <a href={`https://www.clasohlson.com/${product.slug}`}>{product.title}</a>
            </ProductTitle>
            <div className="fade-item">
              <RetTypography fontSize="12px">
                {t('artNr')} {product.sku}
              </RetTypography>
            </div>
            {disabled && (
              <div className="disabled-text">
                <InlineStack spacing="small" alignItems="center" justifyContent="flex-start">
                  <RetTypography fontSize="12px" color="#cc3c3c" weight="bold">
                    {t('returnProductError')}
                  </RetTypography>
                  <AlertCircle />
                  {/* <InfoIcon color="#cc3c3c" fontSize="20" /> */}
                </InlineStack>
              </div>
            )}
          </StyledColumn>
          <StyledColumn width={20} className="align-right price-col fade-item">
            {/* <TextField value={`${product.deliveredQuantity}`} label="Quantity" hideLabel disabled /> */}

            <Box justifyContent="flex-end" alignItems="flex-end" className="price">
              <Price>
                {formatPrice(
                  product.price.sumPriceIncludingDiscounts.priceIncludingVat,
                  getCountryCodeFromUrl(),
                )}
              </Price>
              <Typography variant="caption" tag="span" align="right" color="#666666">
                {t('incVat')}
              </Typography>
            </Box>
          </StyledColumn>
        </CustomRow>
      </SelectableBox>
    </RowWrapper>
  );
};
